import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  DrugItem,
  FirstCategoryLevel,
  SecondCategoryLevel
} from 'src/api/types';
import {
  extractPageNumber,
  generateCatalogPageLink,
  GeneratedLinkUrlObject,
  generateFirstLevelCategoryPageLink,
  generateSecondLevelCategoryPageLink,
  SearchPageQueryType
} from 'src/utils/links';
import { useRouter } from 'next/router';
import usePostSearchFilters from 'src/api/hooks/usePostSearchFilters';
import { isClientSide } from 'src/utils/constants';
import { ItemListName } from 'src/api/analytic.types';
import { getAnalyticPreparedDrugList } from 'src/hooks/useAnalytics/useAnalytics.helpers';

import dynamic from 'next/dynamic';
import Skeleton from 'src/shared-components/Loaders/Skeleton';
import { delayWrapper } from 'src/utils/delayWrapper';
import { useIsMounted } from 'src/hooks/useIsMounted';
import Page from '../../shared-components/Page/Page';
import trns from './trns';
import {
  PostDrugRequest,
  PostDrugResponse
} from '../../api/requests/postDrugSearch';

const BreadCrumbs = dynamic(
  () =>
    delayWrapper(
      import('src/shared-components/design-system/BreadCrumbs').then(
        (file) => file.BreadCrumbs
      )
    ),
  {
    loading: () => <Skeleton width={'100%'} height={32} />
  }
);

const SearchResultsSection = dynamic(
  () =>
    delayWrapper<typeof SearchResultsSection>(
      import('../search/components/SearchResultsSection')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

const Constraints = dynamic(
  () =>
    delayWrapper<typeof Constraints>(
      import('src/shared-components/Constraints/Constraints')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={600} />
  }
);

const DoctorCardContainer = dynamic(
  () =>
    delayWrapper<typeof DoctorCardContainer>(
      import('../home/components/DoctorCardContainer/DoctorCardContainer')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={64} />
  }
);

const FAQ = dynamic(
  () => delayWrapper<typeof FAQ>(import('./components/FAQ')),
  {
    loading: () => <Skeleton width={'100%'} height={64} />
  }
);

const ShowMoreSeoText = dynamic(
  () =>
    delayWrapper<typeof ShowMoreSeoText>(
      import('./components/ShowMoreSeoText')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={64} />
  }
);

const DrugTable = dynamic(
  () => delayWrapper<typeof DrugTable>(import('./components/DrugTable')),
  {
    loading: () => <Skeleton width={'100%'} height={300} />
  }
);

const ViewedDrugsContainer = dynamic(
  () =>
    delayWrapper<typeof ViewedDrugsContainer>(
      import('../../shared-components/ViewedDrugs/ViewedDrugsContainer')
    ),
  {
    loading: () => <Skeleton width={'100%'} height={120} />
  }
);

interface Props {
  drugList: DrugItem[];
  multyLevelCategoriesList?: FirstCategoryLevel[];
  selectedFirstLevelCategory?: FirstCategoryLevel;
  selectedSecondLevelCategory?: SecondCategoryLevel;
  totalDrugsCount: number;
  selectedPages: number[];
  isLoading: boolean;
  isSEOTextHidden: boolean;
  handleLoadMoreSubmit: (page: number) => void;
  finalEntityName: string;
  popularDrugs: PostDrugResponse;
  cheapDrugs: PostDrugResponse;
  searchObject: PostDrugRequest;
  searchCount: number;
}

const CategoryContainerSecondLevel: FC<Props> = ({
  drugList,
  multyLevelCategoriesList,
  selectedFirstLevelCategory,
  selectedSecondLevelCategory,
  totalDrugsCount,
  selectedPages,
  isLoading,
  isSEOTextHidden,
  handleLoadMoreSubmit,
  finalEntityName,
  popularDrugs,
  searchObject,
  searchCount
}) => {
  const router = useRouter();
  const intl = useIntl();
  const { isMounted } = useIsMounted();
  const { data: filtersData, isLoading: isFiltersLoading } =
    usePostSearchFilters(searchObject);

  const drugListWithAnalyticData = useMemo(
    () =>
      getAnalyticPreparedDrugList({
        drugs: drugList,
        itemListName: ItemListName.Category
      }),
    [drugList, searchCount]
  );

  const getPaginationLink = (page: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { nextParams, ...otherQueryParams } = router.query;

    return generateSecondLevelCategoryPageLink({
      ...otherQueryParams,
      category: selectedFirstLevelCategory,
      subCategoryId: selectedSecondLevelCategory?.translitName,
      page
    });
  };

  const handleFilterRouting = (
    query: SearchPageQueryType
  ): GeneratedLinkUrlObject => {
    const { nextParams } = router.query;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, page] = nextParams as string[];
    const pageNumber = extractPageNumber(page);

    const {
      // eslint-disable-next-line
      // @ts-ignore
      category: paramCategory,
      page: paramPage,
      // eslint-disable-next-line
      // @ts-ignore
      nextParams: paramParams,
      ...otherQueryParams
    } = query;

    const categoryRouteObject = generateSecondLevelCategoryPageLink({
      category: selectedFirstLevelCategory,
      subCategoryId: selectedSecondLevelCategory?.translitName,
      page: paramPage || pageNumber
    });
    return {
      pathname: categoryRouteObject.pathname,
      query: otherQueryParams
    };
  };

  const breadCrumbsItems = [
    {
      id: 'home',
      title: intl.formatMessage(trns.homeBreadCrumb),
      href: '/'
    },
    {
      id: 'catalog',
      title: intl.formatMessage(trns.catalogBreadCrumb),
      href: generateCatalogPageLink()
    },
    {
      id: selectedFirstLevelCategory?.translitName ?? '',
      title: selectedFirstLevelCategory?.name ?? '',
      href: generateFirstLevelCategoryPageLink({
        category: selectedFirstLevelCategory
      })
    },
    {
      id: selectedSecondLevelCategory?.translitName ?? '',
      title: selectedSecondLevelCategory?.name ?? '',
      href: generateSecondLevelCategoryPageLink({
        category: selectedFirstLevelCategory,
        subCategoryId: selectedSecondLevelCategory?.translitName
      })
    }
  ];

  return (
    <Page
      initialCategory={multyLevelCategoriesList[0]}
      selectedSubCategoryId={
        multyLevelCategoriesList[0].subCategories[0].translitName
      }
    >
      <BreadCrumbs items={breadCrumbsItems} />
      <Constraints className="pb-12 w-[90%]">
        <h1 className="text-darkBlue fs-header6-bold px-4 pt-6 pb-4.5 lm:pt-14 lm:pb-6 uppercase">
          {selectedSecondLevelCategory.name || ''}
        </h1>
        <SearchResultsSection
          filters={filtersData?.filters || []}
          drugList={drugListWithAnalyticData}
          selectedFirstLevelCategory={selectedFirstLevelCategory}
          selectedSecondLevelCategory={selectedSecondLevelCategory}
          totalDrugsCount={totalDrugsCount}
          selectedPages={selectedPages}
          isLoading={isLoading}
          handleLoadMoreSubmit={handleLoadMoreSubmit}
          getPaginationLink={getPaginationLink}
          pageLinkGenerationMethod={handleFilterRouting}
          isFiltersLoading={isFiltersLoading}
          isTradenameBannerVisible
        />
      </Constraints>
      <div className="flex pt-8">
        <ViewedDrugsContainer />
      </div>
      <Constraints className="pb-12 w-[90%] hidden lm:block">
        {!isSEOTextHidden && (
          <div className="pt-24">
            {selectedSecondLevelCategory?.name &&
            selectedSecondLevelCategory?.description ? (
              <ShowMoreSeoText
                title={selectedSecondLevelCategory?.name}
                text={selectedSecondLevelCategory?.description}
              />
            ) : null}
            {selectedSecondLevelCategory?.doctorReviewer ? (
              <DoctorCardContainer
                doctorReviewer={selectedSecondLevelCategory.doctorReviewer}
              />
            ) : null}
            <DrugTable
              drugList={drugListWithAnalyticData}
              selectedFirstLevelCategory={selectedFirstLevelCategory}
              selectedSecondLevelCategory={selectedSecondLevelCategory}
            />
            <FAQ
              finalEntityName={finalEntityName}
              popularDrugs={popularDrugs}
              cheapDrugs={popularDrugs}
            />
          </div>
        )}
      </Constraints>
      {/* isClientSide prevents these components from duplication in page source */}
      {!isSEOTextHidden && isClientSide && isMounted && (
        <Constraints className="pb-12 w-[90%] block lm:hidden">
          {selectedSecondLevelCategory?.name &&
          selectedSecondLevelCategory?.description ? (
            <ShowMoreSeoText
              title={selectedSecondLevelCategory?.name}
              text={selectedSecondLevelCategory?.description}
            />
          ) : null}
          {selectedSecondLevelCategory?.doctorReviewer ? (
            <DoctorCardContainer
              doctorReviewer={selectedSecondLevelCategory.doctorReviewer}
            />
          ) : null}
          <DrugTable
            drugList={drugListWithAnalyticData}
            selectedFirstLevelCategory={selectedFirstLevelCategory}
            selectedSecondLevelCategory={selectedSecondLevelCategory}
          />
          <FAQ
            finalEntityName={finalEntityName}
            popularDrugs={popularDrugs}
            cheapDrugs={popularDrugs}
          />
        </Constraints>
      )}
    </Page>
  );
};

export default CategoryContainerSecondLevel;
